import { z } from "zod";

export const activitySessionStatusSchema = z.enum(["scheduled", "completed", "cancelled"]);
export const scheduleDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Choose a day.")
  .refine(isCalendarDate, "Choose a valid day.");
export const scheduleTimeSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Choose a valid time.")
  .nullable();

export const activitySessionSchema = z
  .object({
    id: z.uuid(),
    activityId: z.uuid(),
    scheduledDate: scheduleDateSchema,
    scheduledTime: scheduleTimeSchema,
    status: activitySessionStatusSchema,
    version: z.number().int().positive(),
    completedAt: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .strict();

export const createActivitySessionInputSchema = z
  .object({
    idempotencyKey: z.uuid(),
    scheduledDate: scheduleDateSchema,
    scheduledTime: scheduleTimeSchema,
    allowDuplicate: z.boolean().default(false),
  })
  .strict();

export const updateActivitySessionInputSchema = z
  .object({
    version: z.number().int().positive(),
    idempotencyKey: z.uuid(),
    scheduledDate: scheduleDateSchema,
    scheduledTime: scheduleTimeSchema,
    allowDuplicate: z.boolean().default(false),
  })
  .strict();

export const sessionMutationInputSchema = z
  .object({
    version: z.number().int().positive(),
    idempotencyKey: z.uuid(),
  })
  .strict();
export const completeSessionInputSchema = z
  .object({
    version: z.number().int().positive(),
    idempotencyKey: z.uuid(),
  })
  .strict();
export const undoSessionCompletionInputSchema = sessionMutationInputSchema;
export const sessionIdParamsSchema = z.object({ sessionId: z.uuid() });
export const activityScheduleParamsSchema = z.object({ activityId: z.uuid() });

export const sessionMutationResponseSchema = z.object({
  session: activitySessionSchema,
  replayed: z.boolean(),
});

export const sessionCompletionEventSchema = z.object({
  id: z.uuid(),
  activityId: z.uuid(),
  sessionId: z.uuid(),
  completedAt: z.string(),
  reversedAt: z.string().nullable(),
});

export const sessionCompletionResponseSchema = sessionMutationResponseSchema.extend({
  completion: sessionCompletionEventSchema,
});

export const planningDateChoiceSchema = z.enum([
  "tomorrow",
  "three_days",
  "one_week",
  "two_weeks",
  "custom",
]);
export const planningOptionsQuerySchema = z.object({ completionId: z.uuid() });
export const planningDateOptionSchema = z.object({
  choice: planningDateChoiceSchema.exclude(["custom"]),
  label: z.string(),
  scheduledDate: scheduleDateSchema,
});
export const planningOptionsResponseSchema = z.object({
  activityId: z.uuid(),
  completionId: z.uuid(),
  today: scheduleDateSchema,
  timeZone: z.string(),
  usedFallbackTimeZone: z.boolean(),
  choices: z.array(planningDateOptionSchema).length(4),
});

export const ratingEventSchema = z.object({
  id: z.uuid(),
  activityId: z.uuid(),
  sessionId: z.uuid(),
  completionEventId: z.uuid(),
  difficulty: z.number().int().min(1).max(5),
  interest: z.number().int().min(1).max(5),
  recordedAt: z.string(),
});

const reflectionSessionFields = {
  scheduledDate: scheduleDateSchema,
  scheduledTime: scheduleTimeSchema,
};

export const reflectionSessionOperationSchema = z.discriminatedUnion("action", [
  z
    .object({
      action: z.literal("add"),
      idempotencyKey: z.uuid(),
      ...reflectionSessionFields,
    })
    .strict(),
  z
    .object({
      action: z.literal("change"),
      sessionId: z.uuid(),
      version: z.number().int().positive(),
      ...reflectionSessionFields,
    })
    .strict(),
  z
    .object({
      action: z.literal("remove"),
      sessionId: z.uuid(),
      version: z.number().int().positive(),
    })
    .strict(),
]);

export const saveCompletionReflectionInputSchema = z
  .object({
    idempotencyKey: z.uuid(),
    difficulty: z.coerce.number().int().min(1).max(5),
    interest: z.coerce.number().int().min(1).max(5),
    sessionOperations: z.array(reflectionSessionOperationSchema).max(30),
  })
  .strict()
  .superRefine((value, context) => {
    const sessionIds = new Set<string>();
    const addKeys = new Set<string>();
    value.sessionOperations.forEach((operation, index) => {
      if (operation.action === "add") {
        if (addKeys.has(operation.idempotencyKey)) {
          context.addIssue({
            code: "custom",
            path: ["sessionOperations", index, "idempotencyKey"],
            message: "Each new session needs a distinct request identifier.",
          });
        }
        addKeys.add(operation.idempotencyKey);
        return;
      }
      if (sessionIds.has(operation.sessionId)) {
        context.addIssue({
          code: "custom",
          path: ["sessionOperations", index, "sessionId"],
          message: "A session can be changed or removed only once per save.",
        });
      }
      sessionIds.add(operation.sessionId);
    });
  });

export const completionReflectionResponseSchema = z.object({
  ratingEvent: ratingEventSchema,
  futureSessions: z.array(activitySessionSchema),
  replayed: z.boolean(),
});

export type ActivitySession = z.infer<typeof activitySessionSchema>;
export type CreateActivitySessionInput = z.infer<typeof createActivitySessionInputSchema>;
export type UpdateActivitySessionInput = z.infer<typeof updateActivitySessionInputSchema>;
export type SessionMutationInput = z.infer<typeof sessionMutationInputSchema>;
export type CompleteSessionInput = z.infer<typeof completeSessionInputSchema>;
export type UndoSessionCompletionInput = z.infer<typeof undoSessionCompletionInputSchema>;
export type SessionMutationResponse = z.infer<typeof sessionMutationResponseSchema>;
export type SessionCompletionEvent = z.infer<typeof sessionCompletionEventSchema>;
export type SessionCompletionResponse = z.infer<typeof sessionCompletionResponseSchema>;
export type PlanningDateChoice = z.infer<typeof planningDateChoiceSchema>;
export type PlanningOptionsResponse = z.infer<typeof planningOptionsResponseSchema>;
export type RatingEvent = z.infer<typeof ratingEventSchema>;
export type ReflectionSessionOperation = z.infer<typeof reflectionSessionOperationSchema>;
export type SaveCompletionReflectionInput = z.infer<typeof saveCompletionReflectionInputSchema>;
export type CompletionReflectionResponse = z.infer<typeof completionReflectionResponseSchema>;

function isCalendarDate(value: string): boolean {
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}
